import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'

const projects = [
  {
    id: 1,
    slug: 'car-rental',
    imageUrl: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800&q=80',
    liveUrl: 'https://car-rental-demo.fackelmeyer.com',
    category: 'mobility',
  },
  {
    id: 2,
    slug: 'craftsman',
    imageUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
    liveUrl: 'https://craftsman-demo.fackelmeyer.com',
    category: 'service',
  },
  {
    id: 3,
    slug: 'restaurant',
    imageUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
    liveUrl: 'https://restaurant-demo.fackelmeyer.com',
    category: 'gastronomy',
  },
]

export function Projects() {
  const { t } = useTranslation()
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-32 bg-background-secondary"
      aria-labelledby="projects-heading"
    >
      <div className="container mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-8 sm:mb-12">
            <span className="text-primary font-medium text-base sm:text-lg">{t('projects.subtitle')}</span>
            <h2 id="projects-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2">
              {t('projects.title')}
            </h2>
            <p className="text-foreground-secondary mt-4 max-w-2xl mx-auto">
              {t('projects.description')}
            </p>
          </motion.div>

          {/* Projects Grid */}
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            <AnimatePresence mode="popLayout">
              {projects.map((project) => {
                const features = t(`projects.items.${project.slug}.features`, { returnObjects: true }) as string[]
                const isComingSoon = !project.liveUrl

                return (
                  <motion.div
                    key={project.id}
                    layout
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="hover:-translate-y-2 transition-transform duration-300"
                  >
                    <Card
                      variant="elevated"
                      className="h-full overflow-hidden group flex flex-col"
                    >
                      {/* Project Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={project.imageUrl}
                          alt={t(`projects.items.${project.slug}.title`)}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        {/* Category Badge */}
                        <div className="absolute top-3 left-3">
                          <span className="px-3 py-1 text-xs font-semibold bg-primary text-white rounded-full">
                            {t(`projects.categories.${project.category}`)}
                          </span>
                        </div>

                        {/* Coming Soon Badge */}
                        {isComingSoon && (
                          <div className="absolute top-3 right-3">
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-500 text-white rounded-full flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {t('projects.comingSoon')}
                            </span>
                          </div>
                        )}
                      </div>

                      <CardContent className="p-5 sm:p-6 flex flex-col flex-grow">
                        {/* Title */}
                        <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {t(`projects.items.${project.slug}.title`)}
                        </h3>

                        {/* Tagline */}
                        <p className="text-primary font-medium text-sm mb-3">
                          {t(`projects.items.${project.slug}.tagline`)}
                        </p>

                        {/* Description */}
                        <p className="text-foreground-secondary text-sm mb-4 leading-relaxed">
                          {t(`projects.items.${project.slug}.description`)}
                        </p>

                        {/* Features List */}
                        <ul className="space-y-2 mb-6 flex-grow">
                          {Array.isArray(features) && features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-foreground-secondary">
                              <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Buttons */}
                        <div className="flex flex-col gap-2 mt-auto">
                          {project.liveUrl ? (
                            <a
                              href={project.liveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full py-3 px-4 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg"
                            >
                              <ExternalLink className="w-4 h-4" />
                              {t('projects.viewDemo')}
                            </a>
                          ) : (
                            <button
                              disabled
                              className="w-full py-3 px-4 bg-foreground-secondary/20 text-foreground-secondary font-semibold rounded-lg flex items-center justify-center gap-2 cursor-not-allowed"
                            >
                              <Sparkles className="w-4 h-4" />
                              {t('projects.comingSoonButton')}
                            </button>
                          )}
                          <a
                            href="#contact"
                            className="w-full py-3 px-4 border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all duration-200"
                          >
                            {t('projects.requestSimilar')}
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants} className="text-center mt-12">
            <p className="text-foreground-secondary mb-4">
              {t('projects.customProject')}
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
            >
              {t('projects.discussProject')}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
